import { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { FullPageSpinner } from '@Components/Spinner';
import { useSetError } from '../../redux/modals';
import { awaitJSON, ClientToolsLambdaFetch } from '../../utils/fetch-utils';
import { CpsApprovalStatus, CpsReportDetailsResponse } from './cps-utils';
import { ReadOnlyLineItem } from './ReadOnlyLineItem';
import { SurveyInternalTime } from './SurveyInternalTime';
import { SurveyTableHeaders } from './SurveyTableHeaders';
import { TimeInput } from './TimeInput';

interface ReportReviewProps {
  reportId: string;
  onBack: () => void;
}

const ReportReview = ({ reportId, onBack }: ReportReviewProps) => {
  const setError = useSetError();
  const [report, setReport] = useState<CpsReportDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await ClientToolsLambdaFetch(`/cps/report?id=${encodeURIComponent(reportId)}`);
        const data = await awaitJSON<CpsReportDetailsResponse>(res);
        setReport(data);
      } catch (e) {
        setError({ message: `Failed to load report: ${e.message}`, reportError: e });
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId, setError]);

  const handleApprove = useCallback(async () => {
    setSubmitting(true);
    try {
      await ClientToolsLambdaFetch('/cps/reviewReport', {
        method: 'POST',
        body: { reportId, approvalStatus: CpsApprovalStatus.APPROVED }
      });
      setSuccessMessage('Report approved successfully.');
    } catch (e) {
      setError({ message: `Failed to approve report: ${e.message}`, reportError: e });
    } finally {
      setSubmitting(false);
    }
  }, [reportId, setError]);

  const handleReject = useCallback(async () => {
    if (!rejectionReason.trim()) return;
    setSubmitting(true);
    try {
      await ClientToolsLambdaFetch('/cps/reviewReport', {
        method: 'POST',
        body: {
          reportId,
          approvalStatus: CpsApprovalStatus.REJECTED,
          statusReason: rejectionReason
        }
      });
      setSuccessMessage('Report rejected.');
    } catch (e) {
      setError({ message: `Failed to reject report: ${e.message}`, reportError: e });
    } finally {
      setSubmitting(false);
    }
  }, [reportId, rejectionReason, setError]);

  if (loading) return <FullPageSpinner />;
  if (!report) return <p>Report not found.</p>;

  const totalMinutes = Math.round(report.totalHoursWorked * 60);

  return (
    <div className='cps-report-review'>
      <div className='cps-report-review-header'>
        <Button size={Size.LG} appearance={Appearance.EMPTY} onClick={onBack}>
          &larr; Back to Reports
        </Button>
        <h5>
          {report.user?.name ?? 'Unknown'} — {report.periodStartDate} to {report.periodEndDate}
        </h5>
        <span className='cps-report-review-status'>Status: {report.approvalStatus}</span>
      </div>

      <SurveyTableHeaders />
      {report.clientTimeLogs.map((log, i) => (
        <ReadOnlyLineItem key={`ro-${i}-${log.client?.id ?? ''}`} log={log} />
      ))}

      <hr className='cps-divider' />

      <SurveyInternalTime internalTimeLogs={report.internalTimeLogs} readOnly />

      <div className='cps-grand-total-row'>
        <span className='cps-grand-total-label'>Total</span>
        <TimeInput value={totalMinutes} readOnly isTotal />
      </div>

      {report.statusReason && (
        <div className='cps-report-rejection-reason'>
          <strong>Previous rejection reason:</strong> {report.statusReason}
        </div>
      )}

      <div className='cps-report-review-actions'>
        <Button
          size={Size.LG}
          design={Intent.SUCCESS}
          onClick={() => handleApprove()}
          disabled={submitting}
        >
          Approve
        </Button>
        <Button
          size={Size.LG}
          appearance={Appearance.OUTLINED}
          design={Intent.ERROR}
          onClick={() => setShowRejectInput(true)}
          disabled={submitting}
        >
          Reject
        </Button>
      </div>

      {showRejectInput && (
        <div className='cps-report-reject-form'>
          <Form.Control
            as='textarea'
            rows={3}
            placeholder='Reason for rejection (required)'
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
          />
          <div className='cps-report-reject-form-actions'>
            <Button
              size={Size.LG}
              design={Intent.ERROR}
              onClick={() => handleReject()}
              disabled={submitting || !rejectionReason.trim()}
            >
              Confirm Rejection
            </Button>
            <Button
              size={Size.LG}
              appearance={Appearance.EMPTY}
              onClick={() => {
                setShowRejectInput(false);
                setRejectionReason('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <AlertDialog
        show={!!successMessage}
        onHide={() => {
          setSuccessMessage('');
          onBack();
        }}
        intent={Intent.SUCCESS}
        title='Success'
        body={successMessage}
        primaryAction={{
          onClick: () => {
            setSuccessMessage('');
            onBack();
          }
        }}
      />
    </div>
  );
};

export { ReportReview };
